import invariant from 'invariant';
import { RenderedGame, RenderedPlayer } from './Game';
import { GameEvent } from './GameEvents';
import {
  applyGameEvents,
  GameComputedState,
  GamePlayerComputedState,
  GamePlayerInfo,
  GameRatingDelta,
  GameType,
  initialGameComputedState,
} from './GameModel';
import { GameUpdateMessage } from './Messages';
import {
  Bid,
  CardWithID,
  ResolutionSelection,
  TributeCardWithID,
} from './Rules';

export type GameViewModel = {
  id: string;
  version: number;
  type: GameType;
  boardSize: number;
  tributeRowSize: number;
  turnsPerAge: number;
  playerInfos: GamePlayerInfo[];

  sequenceID: number;
  gameStartTimestamp: number;
  gameEndTimestamp: number | null;
  ratingDeltas: GameRatingDelta[] | null;
  phaseStartTimestamp: number;
  readyByUserID: { [key: string]: boolean };
  totalThinkingMillisByPlayerID: { [key: string]: number };
  phaseThinkingMillisByPlayerID: { [key: string]: number };

  bidsByPlayerID: { [key: string]: Bid };
  resolvesByPlayerID: { [key: string]: ResolutionSelection };
} & GameComputedState;
export type GamePlayerViewModel = GamePlayerComputedState;

export function constructGameViewModelFromRenderedGame(
  game: RenderedGame,
): GameViewModel {
  const cardsByID = Object.fromEntries(
    Object.entries(game.cardDefIDByID).map(([id, defID]) => [
      id,
      {
        ...game.cardDefinitionsByID[defID],
        id,
      },
    ]),
  );
  const tributeCardsByID = Object.fromEntries(
    Object.entries(game.tributeCardDefIDByID).map(([id, defID]) => [
      id,
      {
        ...game.tributeCardDefinitionsByID[defID],
        id,
      },
    ]),
  );
  const to_card = (cardID: string): CardWithID => cardsByID[cardID];
  const to_tribute_card = (cardID: string): TributeCardWithID =>
    tributeCardsByID[cardID];

  return {
    ...game,
    cardsByID,
    tributeCardsByID,
    playerInfos: game.playerInfos,
    table: game.tableIDs.map(to_card),
    tributeRow: game.tributeRowIDs.map(to_tribute_card),
    basicPile: game.basicPileIDs.map(to_card),
    playerStates: game.playerStates.map((player) =>
      inflatePlayer(player, cardsByID),
    ),

    deck: game.deckIDs.map(to_card),
    tributeDeck: game.tributeDeckIDs.map(to_tribute_card),
    discard: game.discardIDs.map(to_card),
    tributeDiscard: game.tributeDiscardIDs.map(to_tribute_card),
  };
}
function inflatePlayer(
  player: RenderedPlayer,
  cardsByID: { [key: string]: CardWithID },
): GamePlayerViewModel {
  let to_card = (cardID: string): CardWithID => cardsByID[cardID];

  return {
    userID: player.userID,
    counters: player.counters,
    bid: player.bid,
    cards: player.cardIDs.map(to_card),
    selectedCard: player.selectedCardID ? to_card(player.selectedCardID) : null,
  };
}

export function applyGameUpdateToGameViewModel(
  game: GameViewModel,
  update: GameUpdateMessage,
): GameViewModel {
  invariant(
    game.sequenceID === update.targetSequenceID,
    'Game sequenceID mismatch: %s != %s',
    game.sequenceID,
    update.targetSequenceID,
  );

  return {
    ...game,

    ...applyGameEvents(game, update.newEvents),

    sequenceID: update.sequenceID,
    gameStartTimestamp: update.gameStartTimestamp,
    gameEndTimestamp: update.gameEndTimestamp,
    ratingDeltas: update.ratingDeltas ?? game.ratingDeltas,
    phaseStartTimestamp: update.phaseStartTimestamp,
    readyByUserID: update.readyByUserID,
    totalThinkingMillisByPlayerID: update.totalThinkingMillisByPlayerID,
    phaseThinkingMillisByPlayerID: update.phaseThinkingMillisByPlayerID,
    bidsByPlayerID: update.bidsByPlayerID,
    resolvesByPlayerID: update.resolvesByPlayerID,
  };
}

export function updateGameViewModel(
  game: GameViewModel,
  newEvents: GameEvent[],
): GameViewModel {
  return {
    ...game,

    ...applyGameEvents(game, newEvents),
  };
}

export function resetGameViewModel(game: GameViewModel): GameViewModel {
  return {
    ...game,
    ...initialGameComputedState(game),
    gameEndTimestamp: null,
  };
}
